import React, { useEffect, useRef, useState } from "react";
import {
  Typography,
  Stack,
  Button,
  Paper,
  Box,
} from "@mui/material";
import { Poll, Casino } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useGetPoll } from "../../../services/stagemode_api/talent/talent_api";
import { useVoteInPoll } from "../../../services/stagemode_api/viewers/viewers_api";
import { useAuth } from "../../../hooks";
import SocketHandler from "../../../utils/socketHandler";

const InteractionSection = styled(Paper)(() => ({
  padding: "24px",
  borderRadius: "16px",
  backgroundColor: "#1A1A1A",
  color: "#FFFFFF",
  boxShadow: "0 4px 12px rgba(0,0,0,0.6)",
  fontFamily: "Lexend, sans-serif",
  marginBottom: "24px",
}));

const PollOption = styled(Button)<{ selected?: boolean }>(({ selected }) => ({
  width: "100%",
  justifyContent: "space-between",
  padding: "14px 20px",
  backgroundColor: selected ? "#E50914" : "#2A2A2A",
  color: selected ? "#fff" : "#e5e5e5",
  border: selected ? "none" : "1px solid #444",
  borderRadius: "14px",
  fontFamily: "Lexend, sans-serif",
  fontSize: "14px",
  fontWeight: 500,
  textTransform: "none",
  transition: "all 0.2s ease",
  boxShadow: selected ? "0 2px 6px rgba(229, 9, 20, 0.4)" : "none",
  "&:hover": {
    backgroundColor: selected ? "#B20710" : "#3A3A3A",
  },
}));

interface PollSectionProps {
  bookingId: string;
}

const PollSection: React.FC<PollSectionProps> = ({ bookingId }) => {
  const [selectedPollOption, setSelectedPollOption] = useState<number | null>(null);
  const { data: polls, refetch: refetchPoll } = useGetPoll(bookingId);
  const { mutate: voteInPoll } = useVoteInPoll(bookingId);
  const { userInfo } = useAuth();
  const socketSubscribedRef = useRef(false);
  const activePoll = polls?.data?.find((poll) => poll.status === "Open");
  console.log("This is a poll section")

  useEffect(() => {
    // Prevent multiple subscriptions
    if (socketSubscribedRef.current) {
      return;
    }
    console.log("Poll Checking")
    // Get socket instance and subscribe to messages
    const socketHandler = SocketHandler.getInstance();

    socketHandler.subscribeToBooking(bookingId, (message) => {
      console.log(message,"Polling Message")
      if (message?.type === "PollClosed" || message?.type === "Poll") {
        console.log(message,"Poll Message")
        refetchPoll();
      }
    });

    socketSubscribedRef.current = true;

    // Cleanup subscription when component unmounts
    return () => {
      socketHandler.unsubscribeFromBooking(bookingId);
      socketSubscribedRef.current = false;
    };
  }, [bookingId, refetchPoll]);

  const handlePollVote = (optionId: number) => {
    if (!activePoll?.id) return;

    const pollId = activePoll.id.toString();

    if (selectedPollOption !== null && selectedPollOption !== optionId) {
      voteInPoll({
        pollId,
        voteData: {
          user_name: userInfo?.username || "",
          option_id: selectedPollOption,
          status: "Unvote",
        },
      });
    }

    if (selectedPollOption === optionId) {
      setSelectedPollOption(null);
      voteInPoll({
        pollId,
        voteData: {
          user_name: userInfo?.username || "",
          option_id: optionId,
          status: "Unvote",
        },
      });
    } else {
      setSelectedPollOption(optionId);
      voteInPoll({
        pollId,
        voteData: {
          user_name: userInfo?.username || "",
          option_id: optionId,
          status: "Vote",
        },
      });
    }
  };

  return (
    <InteractionSection>
      <Box display="flex" alignItems="center" mb={2}>
        <Poll sx={{ mr: 1, color: "#E50914" }} />
        <Typography
          variant="h6"
          sx={{
            fontSize: "18px",
            fontWeight: 600,
            color: "#fff",
            fontFamily: "Lexend",
          }}
        >
          Live Poll
        </Typography>
      </Box>

      {activePoll ? (
        <>
          <Box mb={2}>
            <Typography
              variant="subtitle2"
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#9CA3AF",
                fontFamily: "Lexend",
              }}
            >
              {`Poll #${activePoll.id}`}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#F3F4F6",
                fontFamily: "Lexend",
              }}
            >
              {activePoll.title}
            </Typography>
          </Box>

          <Stack spacing={1}>
            {activePoll.options?.map((option) => (
              <PollOption
                key={option.id}
                selected={selectedPollOption === option.id}
                onClick={() => handlePollVote(option.id)}
                startIcon={<Casino />}
              >
                {option.option}
              </PollOption>
            ))}
          </Stack>
        </>
      ) : (
        <Typography
          variant="body2"
          sx={{ color: "#9CA3AF", fontFamily: "Lexend", mt: 1 }}
        >
          No active poll at the moment.
        </Typography>
      )}
    </InteractionSection>
  );
};

export default PollSection;
